import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CertificateUploadForm } from "@/components/shared/CertificateUploadForm";
import { FileUpload } from "@/components/shared/FileUpload";
import { ImportEmittedCfeDialog } from "@/pages/tenants/ImportEmittedCfeDialog";
import { dashboardKeys, tenantKeys, tenantMemberKeys } from "@/lib/queryKeys";
import {
  updateTenantBranding,
  updateTenantSucursal,
  uploadCurrentTenantImage,
} from "@/services/authService";
import { dismissDashboardAlert } from "@/services/dashboardService";
import { importEmittedCfes } from "@/services/invoiceService";
import { createTenantInvite } from "@/services/tenantUsersService";
import { ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED } from "@/hooks/useOnboardingPending";

interface BaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Branded wrapper around Radix Dialog with an editorial header — mono
 * eyebrow, big title, optional description. Matches the dark
 * editorial direction without committing to the dark-charcoal
 * surface (modals stay light for readability of forms).
 */
function OnboardingActionShell({
  open,
  onOpenChange,
  title,
  description,
  children,
}: BaseModalProps & {
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="mt-2">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

interface BrandModalProps extends BaseModalProps {
  currentLogoUrl: string | null;
  currentBrandColor: string | null;
}

export function OnboardingBrandModal({
  open,
  onOpenChange,
  currentLogoUrl,
  currentBrandColor,
}: BrandModalProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [color, setColor] = useState<string>(currentBrandColor ?? "#2D6149");

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      // Save logo first (if changed) so a failure there doesn't leave us
      // with a half-applied state with only the color updated.
      if (logoFile) {
        await uploadCurrentTenantImage(logoFile, "both");
      }
      const trimmed = color.trim();
      if (trimmed && trimmed !== currentBrandColor) {
        await updateTenantBranding(trimmed);
      } else if (!trimmed && currentBrandColor) {
        await updateTenantBranding(null);
      }
    },
    onSuccess: () => {
      toast.success(t("settings.brandColor.success"));
      void queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      onOpenChange(false);
      setLogoFile(null);
    },
    onError: () => {
      toast.error(t("settings.brandColor.error"));
    },
  });

  const noChanges = !logoFile && color.trim() === (currentBrandColor ?? "");

  return (
    <OnboardingActionShell
      open={open}
      onOpenChange={onOpenChange}
      title={t("settings.companyImage.title")}
      description={t("settings.companyImage.helper")}
    >
      <div className="space-y-5">
        <FileUpload
          currentImageUrl={currentLogoUrl}
          onFileChange={setLogoFile}
          label={t("settings.companyImage.logoForInvoice")}
          helperText={t("settings.companyImage.helperInvoice")}
        />

        <div className="space-y-2 border-t pt-4">
          <label className="text-sm font-medium text-[var(--fx-ink-900)]">
            {t("settings.brandColor.title")}
          </label>
          <p className="text-xs text-muted-foreground">
            {t("settings.brandColor.description")}
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder={t("settings.brandColor.hexPlaceholder")}
              aria-label={t("settings.brandColor.hexLabel")}
              className="w-28 rounded-md border border-[var(--fx-ink-200)] bg-white px-3 py-2 text-sm font-[family:var(--fx-ff-mono)]"
            />
            <input
              type="color"
              value={color || "#2D6149"}
              onChange={(e) => setColor(e.target.value)}
              aria-label={t("settings.brandColor.title")}
              title={t("settings.brandColor.pickerHint")}
              className="h-10 w-14 cursor-pointer rounded-md border border-[var(--fx-ink-200)] bg-white p-1"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            className="fx-btn fx-btn-ghost"
            onClick={() => onOpenChange(false)}
            disabled={saveMutation.isPending}
          >
            {t("onboarding.back", "Cancelar")}
          </button>
          <button
            type="button"
            className="fx-btn fx-btn-accent"
            disabled={noChanges || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t("settings.brandColor.saveButton")}
          </button>
        </div>
      </div>
    </OnboardingActionShell>
  );
}

interface SucursalModalProps extends BaseModalProps {
  currentSucursal: number | null;
  onSaved?: () => void;
}

/**
 * Confirm/edit the DGI sucursal (CodSuc). Shown to business self-signup users when
 * GetEntidad didn't auto-detect it at onboarding (fallback to 1). Saving stamps the
 * onboarding-acct-sucursal-confirmed mark so the nudge clears — same mark the
 * accountant checklist uses.
 */
export function OnboardingSucursalModal({
  open,
  onOpenChange,
  currentSucursal,
  onSaved,
}: SucursalModalProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState<string>(
    currentSucursal != null ? String(currentSucursal) : "1",
  );

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const n = Number(value.trim());
      if (!Number.isInteger(n) || n < 1 || n > 99) throw new Error("invalid");
      await updateTenantSucursal(n);
      await dismissDashboardAlert(ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED);
    },
    onSuccess: () => {
      toast.success(
        t("dashboard.onboardingCard.sucursal.saved", "Sucursal confirmada."),
      );
      void queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
      onOpenChange(false);
      onSaved?.();
    },
    onError: (err) => {
      toast.error(
        err instanceof Error && err.message === "invalid"
          ? t(
              "dashboard.onboardingCard.sucursal.invalid",
              "Ingresá un número entero entre 1 y 99.",
            )
          : t(
              "dashboard.onboardingCard.sucursal.error",
              "No se pudo guardar la sucursal.",
            ),
      );
    },
  });

  return (
    <OnboardingActionShell
      open={open}
      onOpenChange={onOpenChange}
      title={t("dashboard.onboardingCard.sucursal.title", "Confirmá tu sucursal DGI")}
      description={t(
        "dashboard.onboardingCard.sucursal.help",
        "Es el código de sucursal (CodSuc) que DGI tiene registrado para tu RUC. Lo usamos en el reporte diario; si no coincide, DGI lo rechaza. Lo encontrás en tus datos registrales de DGI.",
      )}
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <label
            htmlFor="onboarding-sucursal"
            className="text-sm font-medium text-[var(--fx-ink-900)]"
          >
            {t("dashboard.onboardingCard.sucursal.label", "Número de sucursal")}
          </label>
          <input
            id="onboarding-sucursal"
            type="text"
            inputMode="numeric"
            value={value}
            onChange={(e) =>
              setValue(e.target.value.replace(/\D/g, "").slice(0, 2))
            }
            className="w-28 rounded-md border border-[var(--fx-ink-200)] bg-white px-3 py-2 text-sm font-[family:var(--fx-ff-mono)]"
            aria-label={t(
              "dashboard.onboardingCard.sucursal.label",
              "Número de sucursal",
            )}
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            className="fx-btn fx-btn-ghost"
            onClick={() => onOpenChange(false)}
            disabled={saveMutation.isPending}
          >
            {t("onboarding.back", "Cancelar")}
          </button>
          <button
            type="button"
            className="fx-btn fx-btn-accent"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t(
              "dashboard.onboardingCard.sucursal.saveButton",
              "Confirmar sucursal",
            )}
          </button>
        </div>
      </div>
    </OnboardingActionShell>
  );
}

interface CertModalProps extends BaseModalProps {
  tenantRut: string | null;
}

export function OnboardingCertModal({
  open,
  onOpenChange,
  tenantRut,
}: CertModalProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return (
    <OnboardingActionShell
      open={open}
      onOpenChange={onOpenChange}
      title={t("settings.certificate.title")}
      description={t("settings.certificate.description")}
    >
      <CertificateUploadForm
        tenantRut={tenantRut}
        submitButtonLabel={t("settings.certificate.uploadCertificate")}
        showCancel
        onCancel={() => onOpenChange(false)}
        onSuccess={async () => {
          toast.success(t("settings.certificate.configured"));
          await queryClient.invalidateQueries({
            queryKey: tenantKeys.current(),
          });
          onOpenChange(false);
        }}
        idPrefix="onb-cert"
        className="border-0 bg-transparent p-0"
      />
    </OnboardingActionShell>
  );
}

interface CfeModalProps extends BaseModalProps {
  tenantId: string;
  tenantName: string;
}

/**
 * Re-uses the existing settings `ImportEmittedCfeDialog` so the upload
 * UX is identical to /settings. The dashboard CTA opens the same flow,
 * just from a different surface.
 */
export function OnboardingCfeModal({
  open,
  onOpenChange,
  tenantId,
  tenantName,
}: CfeModalProps): JSX.Element {
  return (
    <ImportEmittedCfeDialog
      tenantId={tenantId}
      tenantName={tenantName}
      open={open}
      onOpenChange={onOpenChange}
      importFn={importEmittedCfes}
    />
  );
}

interface InviteAccountantModalProps extends BaseModalProps {
  tenantId: string;
  /** Fires after a successful invite send — used by the card to also dismiss
   *  the "Invitá a tu contador" nudge in localStorage. */
  onInvited?: () => void;
}

/**
 * Inline accountant-invite modal — mirrors the cert/CFE/brand pattern so the
 * user never leaves /dashboard for a one-field action. Posts the same
 * createTenantInvite call /users uses; success closes the modal and dismisses
 * the nudge.
 */
export function OnboardingInviteAccountantModal({
  open,
  onOpenChange,
  tenantId,
  onInvited,
}: InviteAccountantModalProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");

  const inviteMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await createTenantInvite(tenantId, {
        email: email.trim().toLowerCase(),
        role: "accountant",
      });
    },
    onSuccess: () => {
      toast.success(t("onboarding.inviteStep.inviteSuccess"));
      void queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.list(tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["tenant-pending-invites", tenantId],
      });
      setEmail("");
      onOpenChange(false);
      onInvited?.();
    },
    onError: () => {
      toast.error(t("onboarding.inviteStep.inviteError"));
    },
  });

  const trimmed = email.trim().toLowerCase();
  const canSubmit = trimmed.length > 0 && /.+@.+\..+/.test(trimmed);

  return (
    <OnboardingActionShell
      open={open}
      onOpenChange={onOpenChange}
      title={t(
        "dashboard.onboardingCard.accountantActionTitle",
        "Invitá a tu contador",
      )}
      description={t(
        "dashboard.onboardingCard.accountantActionSub",
        "Compartí acceso de solo-lectura para que vea cada movimiento sin tener que pedirte nada.",
      )}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !inviteMutation.isPending) inviteMutation.mutate();
        }}
        className="space-y-4"
      >
        <div className="space-y-2">
          <label
            htmlFor="onb-accountant-email"
            className="text-sm font-medium text-[var(--fx-ink-900)]"
          >
            {t("onboarding.inviteStep.emailLabel", "Email del contador")}
          </label>
          <input
            id="onb-accountant-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t(
              "onboarding.inviteStep.emailPlaceholder",
              "contador@ejemplo.com",
            )}
            disabled={inviteMutation.isPending}
            autoComplete="email"
            className="w-full rounded-md border border-[var(--fx-ink-200)] bg-white px-3 py-2 text-sm focus:border-[var(--fx-accent-500)] focus:outline-none"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            className="fx-btn fx-btn-ghost"
            onClick={() => onOpenChange(false)}
            disabled={inviteMutation.isPending}
          >
            {t("onboarding.back", "Cancelar")}
          </button>
          <button
            type="submit"
            className="fx-btn fx-btn-accent"
            disabled={!canSubmit || inviteMutation.isPending}
          >
            {inviteMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t(
              "dashboard.onboardingCard.accountantActionCta",
              "Invitar contador",
            )}
          </button>
        </div>
      </form>
    </OnboardingActionShell>
  );
}
